import React, { FC, useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/core';
import some from 'lodash/some';
import { EventDTO } from '@atlassiansox/phobos-client-ts';

import { SLOGraph } from '@/controllers';
import FaultZone from '@/components/composed/slo-card/graph/fault-zone';
import { GraphState } from '@/components/composed/slo-card/graph/types';
import { InfoPanel } from '@/components/composed/info-panel';

import { mapToDataPointEventTotals, populateDailyDataTotals } from '../../common/utils';
import { EXPANDED_GRAPH_WIDTH } from '../../common/constants';

import ErrorBudgetGraph from './error-budget-graph';
import GraphTabs, { GraphEventSource } from './graph-tabs';

const graphRootStyles = css`
  /* Fix the dimensions so they do not change on tab transition. */
  width: ${EXPANDED_GRAPH_WIDTH}px;
  height: 100%;
`;

const ExpandedGraph: FC<SLOGraph & { onEdit: () => void }> = ({
  events,
  error,
  warnings,
  errorBudgetDomain,
  dataPoints,
  errorBudgetDataPoints,
  interimResults,
  onEdit,
  publicId,
  performance,
  lowTrafficThreshold,
}) => {
  const [graphState, setToggleState] = useState<GraphState>(GraphState.ShowErrorBudgetGraph);
  const [graphEventSource, setGraphEventSource] = useState<GraphEventSource>(
    GraphEventSource.Active
  );

  const { data, errorBudgetData } = useMemo(
    () => ({
      data: mapToDataPointEventTotals(dataPoints),
      errorBudgetData: populateDailyDataTotals(errorBudgetDataPoints, dataPoints),
    }),
    [dataPoints, errorBudgetDataPoints]
  );

  const [isInfoPanelOpen, setInfoPanelOpen] = useState<boolean>(false);
  const [infoPanelPayload, setInfoPanelPayload] = useState<any>({});

  const selectionCallback = useCallback(
    (payloads: any) => {
      setInfoPanelOpen(!!payloads?.length);
      setInfoPanelPayload(payloads.length > 0 ? payloads[0].payload : {});
    },
    [setInfoPanelOpen]
  );

  if (error && !(warnings.length > 0)) {
    return (
      <FaultZone
        performance={performance}
        error={error}
        warnings={warnings}
        onSloEdit={onEdit}
        publicId={publicId!}
      />
    );
  }

  const hasEventsFromHistoricalIndicators = events
    ? some(events, (event: EventDTO) => event.isFromHistoricalIndicator)
    : false;

  return (
    <div css={[graphRootStyles]}>
      {(warnings.length > 0 || hasEventsFromHistoricalIndicators) && (
        <GraphTabs
          setToggleState={setToggleState}
          graphState={graphState}
          align="left"
          hasEventsFromHistoricalIndicators={hasEventsFromHistoricalIndicators}
          hasWarnings={warnings.length > 0}
          graphEventSource={graphEventSource}
          onToggleHistoricalIndicators={(value: string[]) => {
            setGraphEventSource(value[0] as GraphEventSource);
          }}
        />
      )}

      {graphState === GraphState.ShowWarnings ? (
        error &&
        warnings.length > 0 && (
          <FaultZone
            performance={performance}
            error={error}
            warnings={warnings}
            onSloEdit={onEdit}
            publicId={publicId!}
          />
        )
      ) : (
        <div>
          <ErrorBudgetGraph
            data={data}
            errorBudgetData={errorBudgetData}
            errorBudgetDataDomain={errorBudgetDomain}
            interimResults={interimResults}
            selectionCallback={selectionCallback}
            showClickPrompt={!isInfoPanelOpen}
            lowTrafficThreshold={lowTrafficThreshold}
            graphEventSource={graphEventSource}
          />
          <InfoPanel
            open={isInfoPanelOpen}
            payload={infoPanelPayload}
            graphEventSource={graphEventSource}
            onClose={() => {
              setInfoPanelOpen(!isInfoPanelOpen);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(ExpandedGraph);
